import {
  BusinessOutlined as BusinessIcon,
  Close as CloseIcon,
  ShoppingCartOutlined as OrdersIcon,
  PersonOutline as PatientIcon,
  PeopleOutline as PeopleIcon,
  AssignmentTurnedInOutlined as RecordTxIcon,
  AttachMoneyOutlined as SalesIcon,
  Search as SearchIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  ExitToApp,
  KeyboardArrowDown,
  CloudDownload,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Dialog,
  IconButton,
  LinearProgress,
  ListItemIcon,
  MenuItem,
  Slide,
  TablePagination,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { KPIType } from "./TotalActiveUsersCard";
import Button from "../common/Button";
import StyledMenu from "../common/StyledMenu";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import { getPracticesByUserRoleUserSlice, useUserReducer } from "../../redux/slice/userSlice";
import { searchPatients, SearchPatientRequest } from "../../redux/slice/patient/patientSlice";
import { convertUTCtoLocal } from "../../utils/Utility";
import { SearchKpiRequest, searchPracticesKpiList, searchUserKpiList, searchWeeklySalesKpiList } from "../../redux/slice/dashboard/dashboardSlice";
import ExportToExcel from "../common/ExportToExcel";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { selectIsValueFoundInToken } from "../../redux/slice/authSlice";
import { CAN_ACCESS_KPI_EXPORT_CONSTANTS } from "../../constants/applicationConstants";
import WarningPopup, { WarningPopupObjectProps } from "../common/WarningPopup";
import { useModalStyles } from "../common/useModalStyles";
import { TransitionProps } from "@mui/material/transitions";

interface ColumnConfig {
  id: string;
  label: string;
  minWidth?: number;
  align?: "left" | "right" | "center";
  format?: (value: any) => string;
  type?: "text" | "number" | "currency" | "percentage" | "date" | "badge";
}

interface KPIDetailModalProps {
  open: boolean;
  onClose: () => void;
  kpiType: KPIType;
  onExport?: (data: any[], kpiType: KPIType) => void;
  totalValue?: number | string;
}

const kpiConfig: Record<KPIType, { title: string; icon: React.ReactNode; color: string; columns: ColumnConfig[] }> = {
  activeUsers: {
    title: "Active Users", icon: <PeopleIcon />, color: "#10b981",
    columns: [
      { id: "fullName",        label: "Full Name",     minWidth: 180, type: "text"  },
      { id: "userRole",        label: "User Role",     minWidth: 140, type: "badge" },
      { id: "lastActivityDate",label: "Last Activity", minWidth: 140, type: "date"  },
    ],
  },
  activePractices: {
    title: "Active Practices", icon: <BusinessIcon />, color: "#3b82f6",
    columns: [
      { id: "practiceName", label: "Practice Name", minWidth: 400, type: "text"   },
      { id: "ivrCount",     label: "# of IVRs",    minWidth: 100, align: "center", type: "number" },
      { id: "orderCount",   label: "# of Orders",  minWidth: 100, align: "center", type: "number" },
    ],
  },
  totalPatients: {
    title: "Total Patients", icon: <PatientIcon />, color: "#06b6d4",
    columns: [
      { id: "fullName",           label: "Full Name",           minWidth: 180, type: "text" },
      { id: "associatedPractice", label: "Associated Practice", minWidth: 200, type: "text" },
    ],
  },
  weeklySales: {
    title: "Weekly Sales", icon: <SalesIcon />, color: "#f59e0b",
    columns: [
      { id: "practiceName",          label: "Practice Name",     minWidth: 250, type: "text"       },
      { id: "totalSalesAmount",      label: "Total Sales",       minWidth: 130, align: "center", type: "currency",    format: (v) => `$${v.toLocaleString()}` },
      { id: "numberOfOrders",        label: "# of Orders",       minWidth: 100, align: "center", type: "number"      },
      { id: "percentageOfTotalSales",label: "% of Total",        minWidth: 140, align: "center", type: "percentage",  format: (v) => `${v.toFixed(1)}%`       },
    ],
  },
  weeklyOrders: {
    title: "Weekly Orders", icon: <OrdersIcon />, color: "#8b5cf6",
    columns: [
      { id: "practiceName",  label: "Practice Name",    minWidth: 400, type: "text"     },
      { id: "totalOrders",   label: "Total Orders",     minWidth: 120, align: "center", type: "number"   },
      { id: "totalOrderValue",label: "Total Order Value",minWidth: 130, align: "center", type: "currency", format: (v) => `$${v.toLocaleString()}` },
    ],
  },
  recordTx: {
    title: "Record TX", icon: <RecordTxIcon />, color: "#10b981",
    columns: [
      { id: "practiceName",     label: "Practice Name", minWidth: 200, type: "text"      },
      { id: "totalItemsOrdered",label: "Items Ordered", minWidth: 120, align: "right", type: "number"    },
      { id: "itemsApplied",     label: "Items Applied", minWidth: 120, align: "right", type: "number"    },
      { id: "recordTxPercentage",label: "Record TX %",  minWidth: 160, align: "right", type: "percentage", format: (v) => `${v.toFixed(1)}%` },
    ],
  },
};

const formatCurrency = (v: number) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const KPIDetailModal: React.FC<KPIDetailModalProps> = ({ open, onClose, kpiType, onExport, totalValue }) => {
  const { isDark, tokens } = useModalStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();
  const { practiceByUserRoleUserSlice } = useUserReducer();

  const [page,         setPage]         = useState(0);
  const [rowsPerPage,  setRowsPerPage]  = useState(10);
  const [orderBy,      setOrderBy]      = useState("");
  const [order,        setOrder]        = useState<"asc" | "desc">("asc");
  const [searchQuery,  setSearchQuery]  = useState("");
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);
  const [tableData,    setTableData]    = useState<any[]>([]);
  const [displayData,  setDisplayData]  = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading,    setIsLoading]    = useState(false);
  const [searchCriteria,       setSearchCriteria]       = useState<SearchKpiRequest>({ downloadState: false, currentPage: 1, itemsPerPage: 10 });
  const [patientSearchCriteria,setPatientSearchCriteria] = useState<SearchPatientRequest>({ firstName: "", lastName: "", isSurgicalPracticesAvailable: false, patientStatus: [], practiceIds: [], currentPage: 1, itemsPerPage: 10 });
  const [showPHIWarning, setShowPHIWarning] = useState<WarningPopupObjectProps>({ show: false, message: "" });

  const config = kpiConfig[kpiType];

  const canAccessExportActiveUsersKPI      = useSelector((s: RootState) => selectIsValueFoundInToken("canAccessExportKPIExcel", CAN_ACCESS_KPI_EXPORT_CONSTANTS.ACTIVE_USERS)(s));
  const canAccessExportActivePracticesKPI  = useSelector((s: RootState) => selectIsValueFoundInToken("canAccessExportKPIExcel", CAN_ACCESS_KPI_EXPORT_CONSTANTS.ACTIVE_PRACTICES)(s));
  const canAccessExporTotalPatientsKPI     = useSelector((s: RootState) => selectIsValueFoundInToken("canAccessExportKPIExcel", CAN_ACCESS_KPI_EXPORT_CONSTANTS.TOTAL_PATIENTS)(s));
  const canAccessExportWeeklySalesKPI      = useSelector((s: RootState) => selectIsValueFoundInToken("canAccessExportKPIExcel", CAN_ACCESS_KPI_EXPORT_CONSTANTS.WEEKLY_SALES)(s));
  const canAccessExportActiveWeeklyOrdersKPI = useSelector((s: RootState) => selectIsValueFoundInToken("canAccessExportKPIExcel", CAN_ACCESS_KPI_EXPORT_CONSTANTS.WEEKLY_ORDERS)(s));

  const canExportCurrentKPI = useMemo(() => {
    switch (kpiType) {
      case "activeUsers":      return canAccessExportActiveUsersKPI;
      case "activePractices":  return canAccessExportActivePracticesKPI;
      case "totalPatients":    return canAccessExporTotalPatientsKPI;
      case "weeklySales":      return canAccessExportWeeklySalesKPI;
      case "weeklyOrders":     return canAccessExportActiveWeeklyOrdersKPI;
      default:                 return false;
    }
  }, [kpiType, canAccessExportActiveUsersKPI, canAccessExportActivePracticesKPI, canAccessExporTotalPatientsKPI, canAccessExportWeeklySalesKPI, canAccessExportActiveWeeklyOrdersKPI]);

  useEffect(() => {
    if (open) { setPage(0); setSearchQuery(""); setOrderBy(""); setTableData([]); setDisplayData([]); setTotalRecords(0); }
  }, [open, kpiType]);

  useEffect(() => {
    dispatch(getPracticesByUserRoleUserSlice(true)).catch(() => {});
  }, [dispatch]);

  const fetchPaginatedData = useCallback(async () => {
    if (!open) return;
    setIsLoading(true);
    try {
      let response: any;
      let rows: any[] = [];
      let total = 0;
      const currentPage = page + 1;

      switch (kpiType) {
        case "activeUsers": {
          const req: SearchKpiRequest = { downloadState: false, currentPage, itemsPerPage: rowsPerPage };
          setSearchCriteria(req);
          response = await dispatch(searchUserKpiList(req));
          rows = response?.payload?.searchActiveUsersData?.content?.map((u: any) => ({
            ...u, fullName: [u.firstName, u.middleName, u.lastName].filter(Boolean).join(" ").trim() || "N/A",
            userRole: u.roleDisplayValue, lastActivityDate: convertUTCtoLocal(u.lastLoginAt),
          })) || [];
          total = response?.payload?.searchActiveUsersData?.totalElements || 0;
          break;
        }
        case "activePractices": {
          const req: SearchKpiRequest = { downloadState: false, currentPage, itemsPerPage: rowsPerPage };
          setSearchCriteria(req);
          response = await dispatch(searchPracticesKpiList(req));
          rows  = response?.payload?.data?.searchActivePracticesData?.content || [];
          total = response?.payload?.data?.searchActivePracticesData?.totalElements || 0;
          break;
        }
        case "totalPatients": {
          const practiceIdList = practiceByUserRoleUserSlice?.map((p) => Number(p.practiceId)) || [];
          const req: SearchPatientRequest = { firstName: "", lastName: "", isSurgicalPracticesAvailable: false, patientStatus: ["ACTIVE"], practiceIds: practiceIdList, currentPage, itemsPerPage: rowsPerPage };
          setPatientSearchCriteria(req);
          response = await dispatch(searchPatients(req));
          rows = response?.payload?.searchPatientData?.content?.map((p: any) => ({
            ...p, fullName: [p.firstName, p.middleName, p.lastName].filter(Boolean).join(" ").trim() || "N/A",
            associatedPractice: p.practiceName,
          })) || [];
          total = response?.payload?.searchPatientData?.totalElements || 0;
          break;
        }
        case "weeklySales": {
          const req: SearchKpiRequest = { downloadState: false, currentPage, itemsPerPage: rowsPerPage };
          setSearchCriteria(req);
          response = await dispatch(searchWeeklySalesKpiList(req));
          rows  = response?.payload?.data?.searchPracticesWeeklySalesData?.content?.map((s: any) => ({ ...s, totalSalesAmount: formatCurrency(s.totalSalesAmount) })) || [];
          total = response?.payload?.data?.searchPracticesWeeklySalesData?.totalElements || 0;
          break;
        }
        case "weeklyOrders": {
          const req: SearchKpiRequest = { downloadState: false, currentPage, itemsPerPage: rowsPerPage };
          setSearchCriteria(req);
          response = await dispatch(searchWeeklySalesKpiList(req));
          rows  = response?.payload?.data?.searchPracticesWeeklySalesData?.content?.map((o: any) => ({ ...o, totalOrders: o.numberOfOrders, totalOrderValue: formatCurrency(o.totalSalesAmount) })) || [];
          total = response?.payload?.data?.searchPracticesWeeklySalesData?.totalElements || 0;
          break;
        }
        default: rows = []; total = 0;
      }
      setTableData(rows); setDisplayData(rows); setTotalRecords(total);
    } catch (e) {
      console.error(e); setTableData([]); setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [open, kpiType, page, rowsPerPage, dispatch, practiceByUserRoleUserSlice]);

  useEffect(() => { fetchPaginatedData(); }, [fetchPaginatedData]);

  const handleSort = (property: string) => {
    setOrder(orderBy === property && order === "asc" ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredData = useMemo(() => {
    const sorted = orderBy
      ? [...displayData].sort((a, b) => {
          const av = a[orderBy], bv = b[orderBy];
          if (typeof av === "number" && typeof bv === "number") return order === "asc" ? av - bv : bv - av;
          return order === "asc" ? String(av || "").localeCompare(String(bv || "")) : String(bv || "").localeCompare(String(av || ""));
        })
      : displayData;
    if (!searchQuery) return sorted;
    const q = searchQuery.toLowerCase();
    return sorted.filter((r) => Object.values(r).some((v) => String(v).toLowerCase().includes(q)));
  }, [displayData, orderBy, order, searchQuery]);

  const proceedWithExport = () => {
    ExportToExcel({ ...(kpiType === "totalPatients" ? patientSearchCriteria : searchCriteria), downloadState: true }, config.columns, dispatch, kpiType);
    setAnchorElMenu(null);
  };

  const handleExportToExcel = () => {
    if (kpiType === "totalPatients") {
      setShowPHIWarning({
        show: true, title: "PHI Warning",
        message: (<center>This export may contain Protected Health Information (PHI). Once exported, data security is your responsibility. Proceed only if you are authorized and will handle this data in compliance with HIPAA.</center>),
        variant: "warning",
        buttons: [
          { name: "Cancel",  color: "error",   onClick: () => { setShowPHIWarning({ show: false, message: "" }); setAnchorElMenu(null); }, variant: "outlined"  },
          { name: "Proceed", color: "success",  onClick: () => { setShowPHIWarning({ show: false, message: "" }); proceedWithExport();    }, variant: "contained" },
        ],
      });
    } else {
      proceedWithExport();
    }
  };

  const renderCell = (col: ColumnConfig, value: any) => {
    if (value === null || value === undefined) return <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary }}>—</Typography>;

    switch (col.type) {
      case "badge":
        return (
          <Chip label={value} size="small" sx={{
            bgcolor: isDark ? "rgba(107,114,128,.15)" : "#F3F4F6",
            color: isDark ? "#9CA3AF" : "#6B7280",
            fontWeight: 600, fontSize: "0.72rem", height: 24, borderRadius: "6px",
            border: `1px solid ${isDark ? "rgba(107,114,128,.25)" : "#E5E7EB"}`,
          }} />
        );

      case "percentage": {
        const pct = typeof value === "number" ? value : parseFloat(value);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 110 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress variant="determinate" value={Math.min(pct, 100)} sx={{
                height: 5, borderRadius: 3,
                bgcolor: isDark ? "rgba(255,255,255,.07)" : "#E5E7EB",
                "& .MuiLinearProgress-bar": { borderRadius: 3, bgcolor: config.color },
              }} />
            </Box>
            <Typography sx={{ fontSize: "0.76rem", fontWeight: 700, color: config.color, minWidth: 44, textAlign: "right" }}>
              {col.format ? col.format(value) : `${pct.toFixed(1)}%`}
            </Typography>
          </Box>
        );
      }

      case "currency":
        return <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: tokens.textPrimary }}>{col.format ? col.format(value) : `$${value.toLocaleString()}`}</Typography>;

      case "number":
        return (
          <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", bgcolor: isDark ? "rgba(255,255,255,.06)" : "#F3F4F6", fontWeight: 700, fontSize: "0.76rem", color: tokens.textPrimary, px: 1.25, py: 0.4, borderRadius: "6px", minWidth: 36 }}>
            {value.toLocaleString()}
          </Box>
        );

      default:
        return <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: tokens.textPrimary }}>{col.format ? col.format(value) : value}</Typography>;
    }
  };

  const heroBg      = isDark ? "transparent" : "#FAFBFD";
  const heroBdr     = isDark ? "rgba(255,255,255,.05)" : "#F1F5F9";
  const tHeaderBg   = isDark ? "#1C2533"  : "#F8FAFC"; 
  const tHeaderBdr  = isDark ? "rgba(255,255,255,.05)" : "#EEF2F6";
  const rowBg       = isDark ? "rgba(255,255,255,.018)" : "#ffffff";
  const rowBdr      = isDark ? "rgba(255,255,255,.04)" : "#EEF2F6";
  const rowHover    = isDark ? "rgba(255,255,255,.04)" : "#F1F5F9";
  const emptyBg     = isDark ? "rgba(255,255,255,.02)" : "#F8FAFC";
  const footBg      = isDark ? "transparent" : "#FAFBFD";
  const footBdr     = isDark ? "rgba(255,255,255,.05)" : "#F1F5F9";

  return (
    <>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={onClose}
        maxWidth={false}
        PaperProps={{
          elevation: 0,
          sx: {
            ...{
              backgroundColor: isDark ? "#141720" : "#ffffff",
              backgroundImage: "none",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0"}`,
              boxShadow: isDark
                ? "0 32px 80px rgba(0,0,0,0.5)"
                : "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              maxHeight: "92vh",
            },
            width: {
              xs: "98vw",
              sm: "92vw",
              md: "85vw",
              lg: "75vw",
              xl: "65vw",
            },
            maxWidth: {
              xs: "100%",
              sm: 640,
              md: 880,
              lg: 1080,
              xl: 1200,
            },
            m: { xs: 0, sm: 2 },
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: isDark ? "rgba(0,0,0,0.78)" : "rgba(15,23,42,0.5)",
            backdropFilter: "blur(6px)",
          },
        }}
      >
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 3, py: 2.25,
          background: heroBg,
          borderBottom: `1px solid ${heroBdr}`,
          flexShrink: 0,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
            <Box sx={{
              width: 46, height: 46, borderRadius: "13px",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: `${config.color}18`,
              border: `1px solid ${config.color}30`,
              color: config.color,
              "& svg": { fontSize: 24 },
            }}>
              {config.icon}
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: tokens.textPrimary, letterSpacing: "-0.01em" }}>
                  {config.title}
                </Typography>
                {(kpiType === "weeklySales" || kpiType === "weeklyOrders") && totalValue !== undefined && (
                  <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: config.color }}>
                    ({kpiType === "weeklySales"
                      ? `$${typeof totalValue === "number" ? totalValue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : totalValue}`
                      : totalValue})
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
                <Box sx={{
                  width: 6, height: 6, borderRadius: "50%", bgcolor: config.color,
                  animation: "kpi-pulse 2s ease-in-out infinite",
                  "@keyframes kpi-pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.4 } },
                }} />
                <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, fontWeight: 500 }}>
                  {isLoading ? "Loading…" : `${totalRecords.toLocaleString()} records`}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {canExportCurrentKPI && (
              <>
                <Button
                  id="kpi-export-btn"
                  variant="outlined"
                  disableElevation
                  onClick={(e) => setAnchorElMenu(e.currentTarget)}
                  startIcon={<ExitToApp sx={{ fontSize: 16 }} />}
                  endIcon={<KeyboardArrowDown sx={{ fontSize: 16, transition: "transform .25s", transform: anchorElMenu ? "rotate(180deg)" : "none" }} />}
                  sx={{
                    textTransform: "none", fontWeight: 600, fontSize: "0.78rem",
                    borderRadius: "9px", px: 1.75, py: 0.7,
                    color: tokens.textSecondary, borderColor: tokens.border,
                    "&:hover": { borderColor: tokens.accent, color: tokens.accent, background: `${tokens.accent}0a` },
                  }}
                >
                  Export
                </Button>
                <StyledMenu id="kpi-export-menu" MenuListProps={{ "aria-labelledby": "kpi-export-btn" }} anchorEl={anchorElMenu} open={Boolean(anchorElMenu)} onClose={() => setAnchorElMenu(null)}>
                  <MenuItem onClick={handleExportToExcel}>
                    <ListItemIcon><CloudDownload sx={{ fontSize: 18 }} /></ListItemIcon>
                    <Typography variant="inherit" noWrap>Export list</Typography>
                  </MenuItem>
                </StyledMenu>
              </>
            )}

            <IconButton onClick={onClose} size="small" sx={{
              width: 34, height: 34, borderRadius: "9px",
              color: tokens.textSecondary,
              bgcolor: isDark ? "rgba(255,255,255,.05)" : "#F3F4F6",
              transition: "all .2s",
              "&:hover": { bgcolor: isDark ? "rgba(255,255,255,.10)" : "#E5E7EB", transform: "rotate(90deg)" },
            }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{
          flex: 1, overflow: "auto", px: { xs: 1.5, sm: 2.5 }, py: 2, position: "relative",
          opacity: isLoading ? 0.5 : 1, transition: "opacity .2s",
          "&::-webkit-scrollbar": { width: 4, height: 4 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: isDark ? "rgba(255,255,255,.10)" : "#CBD5E1", borderRadius: 4 },
        }}>

          {filteredData.length === 0 ? (
            <Box sx={{
              py: 8, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              background: emptyBg, borderRadius: "12px", border: `1px solid ${rowBdr}`,
            }}>
              <SearchIcon sx={{ fontSize: 40, color: isDark ? "rgba(255,255,255,.10)" : "#D1D5DB", mb: 1.5 }} />
              <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: tokens.textSecondary }}>
                {isLoading ? "Loading data…" : searchQuery ? "No results found" : "No data available"}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: tokens.textSecondary, mt: 0.5, opacity: 0.7 }}>
                {searchQuery ? "Try adjusting your search" : "Check back later"}
              </Typography>
            </Box>
          ) : isMobile ? (

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {filteredData.map((row, i) => (
                <Box key={i} sx={{
                  borderRadius: "12px",
                  background: rowBg,
                  border: `1px solid ${rowBdr}`,
                  overflow: "hidden",
                }}>

                  <Box sx={{ px: 1.75, py: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                    {config.columns.map((col, ci) => (
                      <Box key={col.id} sx={{
                        display: "flex",
                        alignItems: ci === 0 ? "center" : "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                        ...(ci > 0 && {
                          pt: 1,
                          borderTop: `1px solid ${isDark ? "rgba(255,255,255,.05)" : "#F1F5F9"}`,
                        }),
                      }}>
                        <Typography sx={{
                          fontSize: "0.68rem", fontWeight: 700,
                          letterSpacing: "0.06em", textTransform: "uppercase",
                          color: tokens.textSecondary,
                          flexShrink: 0,
                          ...(ci === 0 && { display: "none" }),
                        }}>
                          {col.label}
                        </Typography>
                        <Box sx={{
                          ...(ci === 0 && {
                            "& .MuiTypography-root": {
                              fontSize: "0.9rem !important",
                              fontWeight: "700 !important",
                              color: `${tokens.textPrimary} !important`,
                            },
                          }),
                        }}>
                          {renderCell(col, row[col.id])}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>

          ) : (
            <Box sx={{ minWidth: config.columns.reduce((sum, c) => sum + (c.minWidth || 100) + 16, 0) }}>
              <Box sx={{
                display: "grid",
                gridTemplateColumns: config.columns.map((c) => `minmax(${c.minWidth || 100}px, 1fr)`).join(" "),
                gap: 2, px: 1.75, py: 1.25,
                background: tHeaderBg, border: `1px solid ${tHeaderBdr}`,
                borderRadius: "10px", mb: 1,
                position: "sticky", top: 0, zIndex: 2,
              }}>
                {config.columns.map((col) => (
                  <Box key={col.id} onClick={() => handleSort(col.id)} sx={{
                    display: "flex", alignItems: "center",
                    justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                    gap: 0.5, cursor: "pointer", userSelect: "none",
                  }}>
                    <Typography sx={{
                      fontSize: "0.67rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase",
                      color: orderBy === col.id ? config.color : tokens.textSecondary,
                      transition: "color .15s",
                    }}>
                      {col.label}
                    </Typography>
                    <Box sx={{ display: "flex", opacity: orderBy === col.id ? 1 : 0.35, "& svg": { fontSize: 14, color: orderBy === col.id ? config.color : tokens.textSecondary } }}>
                      {orderBy === col.id ? (order === "asc" ? <ArrowUpIcon /> : <ArrowDownIcon />) : <ArrowDownIcon />}
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                {filteredData.map((row, i) => (
                  <Box key={i} sx={{
                    display: "grid",
                    gridTemplateColumns: config.columns.map((c) => `minmax(${c.minWidth || 100}px, 1fr)`).join(" "),
                    gap: 2, px: 1.75, py: 1.5,
                    background: rowBg, border: `1px solid ${rowBdr}`,
                    borderRadius: "10px", transition: "all .15s",
                    "&:hover": { background: rowHover, borderColor: isDark ? "rgba(255,255,255,.08)" : "#E2E8F0", transform: "translateY(-1px)" },
                  }}>
                    {config.columns.map((col) => (
                      <Box key={col.id} sx={{
                        display: "flex", alignItems: "center",
                        justifyContent: col.align === "right" ? "flex-end" : col.align === "center" ? "center" : "flex-start",
                      }}>
                        {renderCell(col, row[col.id])}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

        </Box>

        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2.5, py: 1.5,
          background: footBg, borderTop: `1px solid ${footBdr}`,
          flexShrink: 0,
        }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.5, borderRadius: "8px", background: isDark ? "rgba(255,255,255,.04)" : "#F3F4F6" }}>
            <Typography sx={{ fontSize: "0.73rem", color: tokens.textSecondary, fontWeight: 500 }}>
              {Math.min(page * rowsPerPage + 1, totalRecords)}–{Math.min((page + 1) * rowsPerPage, totalRecords)}
            </Typography>
            <Typography sx={{ fontSize: "0.73rem", color: isDark ? "rgba(255,255,255,.2)" : "#D1D5DB" }}>of</Typography>
            <Typography sx={{ fontSize: "0.73rem", color: tokens.textSecondary, fontWeight: 600 }}>{totalRecords.toLocaleString()}</Typography>
          </Box>

          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Rows:"
            sx={{
              color: tokens.textPrimary,
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontWeight: 500, fontSize: "0.75rem" },
              "& .MuiSelect-select": { bgcolor: isDark ? "rgba(255,255,255,.05)" : "#F3F4F6", borderRadius: "6px", fontSize: "0.75rem" },
              "& .MuiSelect-icon": { color: tokens.textSecondary },
              "& .MuiIconButton-root": {
                color: tokens.textSecondary,
                bgcolor: isDark ? "rgba(255,255,255,.05)" : "#F3F4F6",
                borderRadius: "7px", mx: 0.4, width: 30, height: 30,
                "&.Mui-disabled": { color: isDark ? "rgba(255,255,255,.12)" : "#D1D5DB", bgcolor: "transparent" },
                "&:hover:not(.Mui-disabled)": { bgcolor: isDark ? "rgba(255,255,255,.10)" : "#E5E7EB" },
              },
            }}
          />
        </Box>
      </Dialog>

      {showPHIWarning.show && (
        <WarningPopup
          open={showPHIWarning.show}
          onClose={() => setShowPHIWarning({ show: false, message: "" })}
          title={showPHIWarning.title}
          message={<>{showPHIWarning.message}</>}
          variant={showPHIWarning.variant}
          buttons={showPHIWarning.buttons}
        />
      )}
    </>
  );
};

export default KPIDetailModal;